/**
 * NX cameras (server migration 070): the cameras an NX server's agent lists,
 * and which of them are CosMon assets. Adding one makes a camera asset under
 * the NX server, heard through its agent — no port forward, no per-camera
 * login. NX says whether it is online and recording.
 *
 * NX auto-add (072), for the whole NX site: CosMon adds every camera its
 * servers list, then each new one. What it added is listed here to retire.
 */

import { Plus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, Button, Drawer } from './components/index.ts';

interface NxCamera {
  id: string;
  name: string;
  ip: string;
  vendor: string;
  model: string;
  status: string;
  recording: string;
  archiveSeconds: number | null;
  /** `server`: the NX server it is under, when that is another of the site's servers. */
  asset: { id: string; label: string; lifecycle: string; linked: boolean; server: string | null } | null;
  /** The server NX has it on (072); null when the agent does not say. */
  onServer: string | null;
  /** Retired in CosMon: auto-add will not bring it back. */
  retired: boolean;
  /** The camera to pre-select for Link ('' for no guess), worked out by the server. */
  guess: string;
  /** Auto-add held it back: it may already be in CosMon under its own route. */
  held: boolean;
}

interface AutoAdd {
  siteId: string;
  siteName: string | null;
  servers: number;
  /** This host's agent says which cameras NX has on it: needed to turn auto-add on. */
  ownership: boolean;
  on: boolean;
  enabledAt: string | null;
  enabledBy: string | null;
}

/** A camera at the NX server's site, reached its own way, that could be linked. */
interface Candidate {
  id: string;
  label: string;
  name: string;
  host: string | null;
}

interface NxCameraList {
  reportedAt: string | null;
  /** null: this host's agent does not say which NX site it is on yet. */
  autoAdd: AutoAdd | null;
  autoAdded: Array<{ id: string; label: string; at: string; by: string }>;
  devices: NxCamera[] | null;
  missing: Array<{ id: string; label: string; deviceId: string }>;
  candidates: Candidate[];
}

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

const days = (s: number | null): string => (s === null ? '' : `${(s / 86400).toFixed(1)} d`);

export function NxCamerasDrawer({
  assetId,
  label,
  onClose,
}: {
  assetId: string;
  label: string;
  onClose: () => void;
}): React.JSX.Element {
  const [list, setList] = useState<NxCameraList | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [linkTo, setLinkTo] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const serverPath = `/api/assets/${assetId}/nx-cameras`;

  const load = useCallback(async () => {
    try {
      setList(await api.get<NxCameraList>(`/api/assets/${assetId}/nx-cameras`));
    } catch (err) {
      setError(failure(err));
    }
  }, [assetId]);

  useEffect(() => {
    void load();
  }, [load]);

  const unbound = list?.devices?.filter((d) => d.asset === null && !d.retired) ?? [];
  const held = list?.devices?.filter((d) => d.held) ?? [];

  const setAutoAdd = async (on: boolean): Promise<void> => {
    if (list?.autoAdd == null) return;
    const site = list.autoAdd.siteName ?? 'this NX site';
    const ask = on
      ? `Add every camera ${site} lists to CosMon now, each under the server NX has it on, and each new one NX announces from here on? A camera NX fails over moves with it. They start active, so cameras NX has offline alert straight away.`
      : `Stop adding ${site}'s cameras automatically? Cameras already added stay.`;
    if (!window.confirm(ask)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.put<{ added?: unknown[]; held?: unknown[]; skipped?: string[] }>(
        `/api/nx-sites/${list.autoAdd.siteId}/auto-add`,
        { on },
      );
      const n = res.added?.length ?? 0;
      const h = res.held?.length ?? 0;
      setDone(
        on
          ? `Auto-add on. Added ${n} camera${n === 1 ? '' : 's'}${h > 0 ? `; ${h} held for you to link or add` : ''}.` +
              (res.skipped?.length ? ` Not added: ${res.skipped.join('; ')}.` : '')
          : 'Auto-add off. Cameras already added stay.',
      );
      await load();
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  /** Undo for auto-add: retire, which also keeps auto-add from adding it again. */
  const retire = async (ids: string[]): Promise<void> => {
    if (ids.length > 1 && !window.confirm(`Retire ${ids.length} cameras added automatically? Auto-add will not add them again.`)) return;
    setBusy(true);
    setError(null);
    try {
      for (const id of ids) await api.del(`/api/assets/${id}`);
      setDone(`Retired ${ids.length} camera${ids.length === 1 ? '' : 's'}. Auto-add will not add ${ids.length === 1 ? 'it' : 'them'} again.`);
      await load();
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string): void =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const add = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ made: Array<{ id: string }> }>(`/api/assets/${assetId}/nx-cameras`, { deviceIds: [...picked] });
      setDone(`Added ${res.made.length} camera${res.made.length === 1 ? '' : 's'}. First readings arrive with the next report.`);
      setPicked(new Set());
      await load();
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  const link = async (deviceId: string, assetId: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`${serverPath}/link`, { deviceId, assetId });
      setDone('Linked. NX’s recording reading arrives with the next report; the camera keeps its own route and pictures.');
      await load();
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  const unlink = async (assetId: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await api.del(`${serverPath}/link/${assetId}`);
      setDone('Unlinked. The camera is judged on its own checks again from the next collector pass.');
      await load();
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={`NX cameras — ${label}`}
      subtitle="Cameras this NX server records, as its agent last listed them."
      width="lg"
      actions={
        <Button size="sm" icon={<Plus size={14} />} disabled={picked.size === 0 || busy} onClick={() => void add()}>
          Add {picked.size > 0 ? picked.size : ''} to CosMon
        </Button>
      }
    >
      <div className="stack-tight">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {done !== null && <Banner tone="ok">{done}</Banner>}
        {list !== null && (
          <div className="row-tight">
            <label className="row-tight">
              <input
                type="checkbox"
                checked={list.autoAdd?.on ?? false}
                disabled={busy || list.autoAdd === null || (!list.autoAdd.on && !list.autoAdd.ownership)}
                onChange={(e) => void setAutoAdd(e.target.checked)}
              />
              Auto-add all cameras reported by NX
            </label>
            <span className="small muted">
              {list.autoAdd === null || !list.autoAdd.ownership
                ? 'Needs an agent that reports its NX site and which cameras NX has on it.'
                : `${list.autoAdd.siteName ?? 'This NX site'}, ${list.autoAdd.servers} server${list.autoAdd.servers === 1 ? '' : 's'}` +
                  (list.autoAdd.on && list.autoAdd.enabledAt !== null
                    ? `: on since ${new Date(list.autoAdd.enabledAt).toLocaleString()}${list.autoAdd.enabledBy ? ` (${list.autoAdd.enabledBy})` : ''}`
                    : '')}
            </span>
          </div>
        )}
        {held.length > 0 && (
          <Banner tone="warn" title="Held back by auto-add">
            {held.map((d) => d.name).join(', ')}: {held.length === 1 ? 'it looks' : 'they look'} like{' '}
            {held.length === 1 ? 'a camera' : 'cameras'} already in CosMon under {held.length === 1 ? 'its' : 'their'} own
            route. Link {held.length === 1 ? 'it' : 'each one'} below, or tick and add {held.length === 1 ? 'it' : 'them'} if not.
          </Banner>
        )}
        {list !== null && list.autoAdded.length > 0 && (
          <details>
            <summary>
              Added automatically: {list.autoAdded.length} camera{list.autoAdded.length === 1 ? '' : 's'} under this server
            </summary>
            <table className="dense">
              <tbody>
                {list.autoAdded.map((a) => (
                  <tr key={a.id}>
                    <td>{a.label}</td>
                    <td className="small muted">
                      {new Date(a.at).toLocaleString()} {a.by === 'agent' ? '(NX announced it)' : '(when turned on)'}
                    </td>
                    <td>
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => void retire([a.id])}>
                        Retire
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => void retire(list.autoAdded.map((a) => a.id))}>
              Retire all {list.autoAdded.length}
            </Button>
          </details>
        )}
        {list !== null && list.devices === null && (
          <Banner tone="info" title="No camera list yet">
            The agent sends one from version 0.9.5, once it has an NX login.
          </Banner>
        )}
        {list !== null && list.candidates.length > 0 && (
          <Banner tone="info" title="This site's cameras are already in CosMon">
            Link each NX camera to its existing asset rather than adding it: the camera keeps its own route, history
            and pictures, and NX adds whether it is recording. The best guess (same address or name) is pre-selected —
            check it before linking.
          </Banner>
        )}
        {list !== null && list.missing.length > 0 && (
          <Banner tone="warn" title="No longer in NX">
            {list.missing.map((m) => m.label).join(', ')}: NX does not list {list.missing.length === 1 ? 'it' : 'them'} any
            more. Retire {list.missing.length === 1 ? 'it' : 'them'} in Assets if removed for good.
          </Banner>
        )}
        {list?.devices != null && list.devices.length === 0 && (
          <Banner tone="warn" title="NX listed no cameras">
            The agent read NX but found no cameras in its answer. If NX does have cameras, run Get NX survey on this
            host and send it over: this NX version may answer differently.
          </Banner>
        )}
        {list?.devices != null && list.devices.length > 0 && (
          <table className="dense">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    aria-label="All not yet added"
                    checked={unbound.length > 0 && picked.size === unbound.length}
                    disabled={unbound.length === 0}
                    onChange={(e) => setPicked(e.target.checked ? new Set(unbound.map((d) => d.id)) : new Set())}
                  />
                </th>
                <th>Name</th>
                <th>Address</th>
                <th>On server</th>
                <th>Model</th>
                <th>NX status</th>
                <th>Recording</th>
                <th>Archive</th>
                <th>In CosMon</th>
              </tr>
            </thead>
            <tbody>
              {list.devices.map((d) => (
                <tr key={d.id}>
                  <td>
                    {d.asset === null && !d.retired && (
                      <input type="checkbox" aria-label={`Add ${d.name}`} checked={picked.has(d.id)} onChange={() => toggle(d.id)} />
                    )}
                  </td>
                  <td>{d.name}</td>
                  <td>{d.ip}</td>
                  <td>{d.onServer ?? ''}</td>
                  <td>{[d.vendor, d.model].filter(Boolean).join(' ')}</td>
                  <td>{d.status}</td>
                  <td>{d.recording === 'On' ? 'on' : 'off in NX'}</td>
                  <td>{days(d.archiveSeconds)}</td>
                  <td>
                    {d.asset !== null && !d.asset.linked && d.asset.label}
                    {d.asset !== null && d.asset.server !== null && <span className="small muted"> (under {d.asset.server})</span>}
                    {d.retired && <span className="small muted">retired; not added again</span>}
                    {d.held && <span className="small muted">held: may be here already </span>}
                    {d.asset !== null && d.asset.linked && (
                      <span className="row-tight">
                        linked: {d.asset.label}
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void unlink(d.asset!.id)}>
                          Unlink
                        </Button>
                      </span>
                    )}
                    {d.asset === null && !d.retired && list.candidates.length > 0 && (
                      <span className="row-tight">
                        <select
                          aria-label={`Link ${d.name} to an existing camera`}
                          value={linkTo[d.id] ?? d.guess}
                          onChange={(e) => setLinkTo((m) => ({ ...m, [d.id]: e.target.value }))}
                        >
                          <option value="">Link to…</option>
                          {list.candidates.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy || (linkTo[d.id] ?? d.guess) === ''}
                          onClick={() => void link(d.id, linkTo[d.id] ?? d.guess)}
                        >
                          Link
                        </Button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {list !== null && (
          <p className="small muted">
            {list.reportedAt === null ? 'Not reported yet.' : `Listed ${new Date(list.reportedAt).toLocaleString()}.`} A camera
            with recording off in NX is watched for being online only.
          </p>
        )}
      </div>
    </Drawer>
  );
}
